import { billing, unwrap } from "../api";
import { formatMoment } from "../format";
import type { PagedResult } from "../../components/table/data-table";
import { isGuid, redirectToCanonical, refundPath } from "../paths";

/**
 * Ported from apps/admin/app/(app)/finance/finance.ts. The vocabulary the Finance screens read,
 * nothing they compute (constitution I) — every figure arrives already computed and rounded.
 */

export type OutstandingRefund = {
  refundId: string;
  /** `RFD-000012` — the refund's URL key (`refundPath`); immutable. */
  refundNumber: string;
  paymentId: string;
  orderId: string;
  amount: number | string;
  reason: string;
  status: string;
  attempts: number | string;
  lastAttemptAt: string | null;
  provider: string;
  providerError: string | null;
  /** Labels Billing sends beside the ids; null when it could not resolve one. Never shown as the id. */
  providerReference?: string | null;
  orderNumber?: string | null;
  customerName?: string | null;
};

export type Payment = {
  paymentId: string;
  orderId: string;
  provider: string;
  providerReference: string;
  status: string;
  amount: number | string;
  refundedTotal: number | string;
  refundableRemaining: number | string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  orderNumber?: string | null;
};

/** Indian-grouping money format for the `number | string` decimal Billing puts on the wire. */
const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
export function money(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "not priced";
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isNaN(n) ? String(value) : MONEY.format(n);
}

export const QUEUED = "queued";
export const FAILED = "failed";
export const SUCCEEDED = "succeeded";

/**
 * The three reasons `POST /payments/{id}/refunds` recognises (`PaymentController.ParseReason`,
 * `RefundReason`). A retry for any other reason would 422 with `unknown_refund_reason` — this
 * screen doesn't guess at a reason it doesn't know, same philosophy as `readPayment`'s unrecognised
 * branch.
 */
export const RECOGNISED_REFUND_REASONS = new Set(["order_cancelled", "short_fulfilment", "duplicate_payment"]);

/**
 * A retry is safe to offer only for a `failed` refund with a reason this screen recognises.
 * `RefundPolicy.Committed` (backend) excludes `Failed` refunds from the aggregate cap, so minting a
 * fresh `requestId` for the same amount is a legitimate new attempt — no money moved on the failed
 * one. A `queued` refund still reserves the cap there, so retrying it is not offered: the API would
 * 422 `refund_exceeds_amount_paid` for the full amount, and the existing `RETRY_SCHEDULE_NOTICE`
 * already tells a person to decide now instead.
 */
export function canRetry(refund: OutstandingRefund): boolean {
  return refund.status === FAILED && RECOGNISED_REFUND_REASONS.has(refund.reason);
}

export type RefundBuckets = { queued: OutstandingRefund[]; failed: OutstandingRefund[]; unrecognised: OutstandingRefund[] };

export function bucketRefunds(refunds: readonly OutstandingRefund[]): RefundBuckets {
  return {
    queued: refunds.filter((r) => r.status === QUEUED),
    failed: refunds.filter((r) => r.status === FAILED),
    unrecognised: refunds.filter((r) => r.status !== QUEUED && r.status !== FAILED),
  };
}

export const RETRY_SCHEDULE_NOTICE =
  "Whether another automatic attempt is coming isn't shown here — decide now: chase it with the " +
  "provider, or escalate it. The money is owed either way.";

export type PaymentReading = { status: string; heading: string; meaning: string; tone: "success" | "destructive" | "warning" | "default" };

const READINGS: Record<string, Omit<PaymentReading, "status">> = {
  succeeded: {
    heading: "Succeeded",
    meaning:
      "The provider confirmed the order's full amount. This, or an authenticated webhook, is what approved the order — a redirect never approves anything.",
    tone: "success",
  },
  failed: {
    heading: "Failed",
    meaning: "The provider answered and refused. No money moved, the order was not placed successfully, and its reservation was released.",
    tone: "destructive",
  },
  referred: {
    heading: "Referred",
    meaning:
      "The provider confirmed an amount that is not the order's. Money moved but not this order's money, so the order was not approved. This is neither a success nor a failure, and the confirmed figure is on the payment's audit entry for whoever settles it.",
    tone: "warning",
  },
  initiated: {
    heading: "Unresolved",
    meaning:
      "Nobody knows yet whether money moved. This is a state of its own — not a success and not a failure — and the service's own sweep actively verifies payments left here beyond its configured age.",
    tone: "warning",
  },
  refunded: { heading: "Refunded in full", meaning: "The payment succeeded and has since been refunded in full.", tone: "default" },
  partially_refunded: { heading: "Partly refunded", meaning: "The payment succeeded and part of it has since been refunded.", tone: "default" },
};

export function readPayment(status: string): PaymentReading {
  const known = READINGS[status];
  if (known) return { status, ...known };
  return {
    status,
    heading: "A status this screen does not recognise",
    meaning:
      "The Billing service reported a status that is not one of the six this screen knows. It is shown above exactly as it arrived and is deliberately not treated as either a success or a failure. Report it — a seventh status is a contract change.",
    tone: "warning",
  };
}

/** `readPayment`'s tone, as the rizzui `Badge` `color` it renders with — shared by every screen that badges a payment's status. */
const TONE_COLOR = { success: "success", destructive: "danger", warning: "warning", default: "secondary" } as const;
export function paymentStatusColor(status: string): "success" | "danger" | "warning" | "secondary" {
  return TONE_COLOR[readPayment(status).tone];
}

export function resolutionSource(resolvedBy: string | null): string {
  if (resolvedBy === "verification") return "the portal's own verify call to the provider";
  if (resolvedBy === "webhook") return "an authenticated webhook from the provider";
  if (resolvedBy === null) return "not resolved yet — nothing has decided this payment";
  return `reported as "${resolvedBy}", which is not one of the two values this screen knows`;
}

export function count(value: number | string): string {
  return String(value);
}

/** Absolute IST instant, never relative — a retry-schedule screen must not imply a next-attempt guess. */
export function moment(iso: string | null | undefined): string {
  if (!iso) return "never attempted";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : formatMoment(iso);
}

/** Everything not yet refunded — or, with `status`, only refunds in that one state (e.g. `SUCCEEDED`). */
export async function getOutstandingRefunds(status?: string): Promise<{ refunds: OutstandingRefund[] }> {
  const data = (await unwrap((await billing()).GET("/api/billing/admin/refunds", { params: { query: status ? { status } : {} } }))) as
    | { refunds: OutstandingRefund[] }
    | undefined;
  if (!data) throw new Error("Billing returned no refund list.");
  return data;
}

export async function getRefundByNumber(refundNumber: string): Promise<OutstandingRefund> {
  return unwrap(
    (await billing()).GET("/api/billing/admin/refunds/by-number/{refundNumber}", { params: { path: { refundNumber } } }),
  ) as Promise<OutstandingRefund>;
}

/**
 * The refund a route's `:refundNumber` names. Billing has no by-id read, so a legacy id is looked up
 * in the outstanding list and redirected to its number URL, query kept; an id that is not there
 * (a refund since completed) resolves to null, and the screen says what was checked.
 */
export async function refundByKey(request: Request, key: string): Promise<OutstandingRefund | null> {
  if (!isGuid(key)) return getRefundByNumber(key);
  const row = (await getOutstandingRefunds()).refunds.find((refund) => refund.refundId.toLowerCase() === key.toLowerCase());
  if (!row?.refundNumber) return row ?? null; // no number yet (backend not upgraded): open in place
  throw redirectToCanonical(request, key, refundPath(row.refundNumber));
}

/** `components["schemas"]["RefundResponse"]`, ported rather than imported (same convention as `Payment`/`OutstandingRefund`). */
export type RefundResponse = {
  refundId: string;
  /** Not declared by the payment route's response; read when Billing sends it. */
  refundNumber?: string | null;
  paymentId: string;
  requestId: string;
  amount: number | string;
  reason: string;
  status: string;
  providerRefundReference: string | null;
  providerError: string | null;
  attempts: number | string;
  lastAttemptAt: string | null;
};

/**
 * `POST /payments/{id}/refunds` with a caller-minted `requestId` (FR-019, payments.md rule 2). A
 * repeat of the same `requestId` is answered with the same refund (200), never a second one — the
 * server's own idempotency key, not anything this screen enforces.
 */
export async function retryRefund(
  paymentId: string,
  body: { requestId: string; amount: number | string; reason: string; reasonNote: string | null },
): Promise<RefundResponse> {
  const data = (await unwrap(
    // The amount is the API's own figure, re-sent; a parse to the documented number, not arithmetic.
    (await billing()).POST("/api/billing/payments/{id}/refunds", { params: { path: { id: paymentId } }, body: { ...body, amount: Number(body.amount) } }),
  )) as RefundResponse | undefined;
  if (!data) throw new Error("Billing returned no refund for that retry.");
  return data;
}

export async function getPayment(id: string): Promise<Payment> {
  const data = (await unwrap(
    (await billing()).GET("/api/billing/payments/{id}", { params: { path: { id } } }),
  )) as Payment | undefined;
  if (!data) throw new Error("Billing returned no payment for that identifier.");
  return data;
}

/** PaymentRow (FIN-05) — one row of the Accounts transactions list, `GET /admin/payments`. */
export type PaymentRow = {
  paymentId: string;
  orderId: string;
  provider: string;
  providerReference: string;
  amount: number | string;
  status: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  initiatedAt: string;
  orderNumber?: string | null;
  customerName?: string | null;
  invoiceNumber?: string | null;
};

/** Every status `readPayment` above recognises, in wire spelling — the fixed vocabulary the list's status dropdown offers. */
export const PAYMENT_STATUSES = ["initiated", "succeeded", "failed", "referred", "refunded", "partially_refunded"] as const;

/**
 * `GET /admin/payments` (FIN-05, GAP-8) — server-paged, with a genuinely narrowing date range and
 * status, same shape as `listInvoices`. `from`/`to` are `yyyy-MM-dd`.
 */
export async function listPayments(params: {
  page: number;
  pageSize?: number;
  from?: string;
  to?: string;
  status?: string;
}): Promise<PagedResult<PaymentRow>> {
  const data = (await unwrap(
    (await billing()).GET("/api/billing/admin/payments", {
      params: { query: { page: params.page, pageSize: params.pageSize ?? 25, from: params.from, to: params.to, status: params.status } },
    }),
  )) as PagedResult<PaymentRow> | undefined;
  if (!data) throw new Error("Billing returned no payment list.");
  return data;
}
